import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiChevronDown, FiHeart, FiMinus, FiPlus, FiShoppingBag, FiTruck } from "react-icons/fi";
import productData from "../Datas/productData";
import { CartContext } from "./CartContext";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/productdetails.scss";

const ProductDetail = () => {
  const { id } = useParams();
  const product = productData.find((item) => item.id === Number(id));
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!product) return;
    setSelectedSize(product.size?.[0] || "");
    setSelectedColor(product.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
    setAdded(false);
  }, [product]);

  const relatedProducts = useMemo(
    () => productData.filter((item) => item.category === product?.category && item.id !== product?.id).slice(0, 4),
    [product]
  );

  if (!product) {
    return (
      <main className="product-not-found">
        <p className="eyebrow">404 · lost between the pages</p>
        <h1>We couldn’t find that piece.</h1>
        <p>The product may have moved or quietly left the collection.</p>
        <Link className="button" to="/shop">Return to the shop</Link>
      </main>
    );
  }

  const images = product.images?.length ? product.images : [product.image];
  const attributeStory = Object.entries(product.attributes)
    .map(([label, value]) => `${label.toLowerCase()} ${value}`)
    .join(", ");
  const categoryNotes = {
    "Notebooks & Journals": "It is equally at home beside a morning coffee, in a work bag or open on a quiet evening desk. The paper and binding were chosen for regular writing rather than display, so the book becomes more personal as it gathers marks and wear.",
    "Memos & Sticky Notes": "Keep it within reach for quick lists, page markers and fragments of ideas. Its compact proportions make it easy to move between a home desk, studio table and everyday carry.",
    "Tapes & Stickers": "Use it sparingly as a small accent or build overlapping layers for a fuller journal spread. The restrained palette pairs naturally with plain paper, photographs and handwritten notes.",
    "Stamps": "The motif is designed to work alone or repeat into borders, clusters and simple visual rhythms. Small variations in pressure and ink make every impression feel pleasantly individual.",
    "Writing & Drawing": "Balanced for everyday notes and relaxed drawing, it supports both deliberate lines and quick passing ideas. Its approachable proportions make it a dependable tool to reach for throughout the day.",
    "Paper Goods": "Made for messages worth keeping, it offers enough visual character without competing with your handwriting. Use it for correspondence, small gifts or quiet additions to a journal.",
    "Desk Essentials": "Its form is intentionally simple, allowing it to sit comfortably among books, paper and familiar tools. Built for repeat use, it brings a little order without making the desk feel overly arranged.",
    "Carriers & Accessories": "Sized for the tools used most often, it keeps daily essentials together without unnecessary bulk. The materials are intended to soften naturally with handling and travel.",
  };

  const handleAddToCart = () => {
    addToCart(product.id, quantity, selectedSize, selectedColor);
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2400);
  };

  return (
    <main className="product-detail">
      <nav className="product-breadcrumb page-shell" aria-label="Breadcrumb">
        <Link to="/">Home</Link><span>/</span><Link to="/shop">Shop</Link><span>/</span><span>{product.name}</span>
      </nav>

      <div className="product-detail__container page-shell">
        <section className="product-gallery" aria-label="Product images">
          <div className="product-thumbs">
            {images.map((image, index) => (
              <button
                key={image}
                type="button"
                className={index === selectedImage ? "is-active" : ""}
                onClick={() => setSelectedImage(index)}
                aria-label={`Show ${product.name} image ${index + 1}`}
              >
                <img src={image} alt="" width="120" height="120" />
              </button>
            ))}
          </div>
          <div className="product-main-image">
            <img src={images[selectedImage]} alt={product.name} width="1200" height="1200" />
          </div>
        </section>

        <aside className="product-info">
          <p className="product-brand">{product.brand}</p>
          <h1>{product.name}</h1>
          <div className="product-price-detail">
            {product.compareAtPrice && <s>{formatPrice(product.compareAtPrice)}</s>}
            <span>{formatPrice(product.price)}</span>
          </div>
          <p className="tax-note">Tax included. Shipping calculated at checkout.</p>

          <div className="product-rating" aria-label={`${product.rating} out of 5 stars from ${product.reviews} reviews`}>
            <span aria-hidden="true">★★★★★</span>
            <small>{product.rating} · {product.reviews} reviews</small>
          </div>

          <div className="product-narrative">
            <p className="product-lead">{product.description}</p>
            <p>{categoryNotes[product.category] || "Created as a useful everyday object, it balances quiet appearance with practical details and comfortable handling."}</p>
            <p className="product-material-note"><strong>Material notes</strong> — {attributeStory}. Key details include {product.features.map((feature) => feature.toLowerCase()).join(", ")}.</p>
          </div>

          {product.colors?.length > 0 && (
            <label className="product-select">
              <span>Colour <strong>{selectedColor}</strong></span>
              <div>
                <select value={selectedColor} onChange={(event) => setSelectedColor(event.target.value)} aria-label="Choose colour">
                  {product.colors.map((color) => <option key={color}>{color}</option>)}
                </select>
                <FiChevronDown aria-hidden="true" />
              </div>
            </label>
          )}

          {product.size?.length > 0 && (
            <label className="product-select">
              <span>Format <strong>{selectedSize}</strong></span>
              <div>
                <select value={selectedSize} onChange={(event) => setSelectedSize(event.target.value)} aria-label="Choose format">
                  {product.size.map((size) => <option key={size}>{size}</option>)}
                </select>
                <FiChevronDown aria-hidden="true" />
              </div>
            </label>
          )}

          <div className="product-purchase-row">
            <div className="quantity-picker" aria-label="Quantity selector">
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} aria-label="Decrease quantity"><FiMinus /></button>
              <span>{quantity}</span>
              <button type="button" onClick={() => setQuantity((value) => value + 1)} aria-label="Increase quantity"><FiPlus /></button>
            </div>
            <button type="button" className="add-to-cart" onClick={handleAddToCart}>
              <FiShoppingBag /> {added ? "Added to bag" : "Add to bag"}
            </button>
            <button
              type="button"
              className={`product-wishlist${isWishlisted(product.id) ? " is-active" : ""}`}
              onClick={() => toggleWishlist(product.id)}
              aria-label={`${isWishlisted(product.id) ? "Remove from" : "Add to"} wishlist`}
            ><FiHeart /></button>
          </div>

          <div className="stock-note"><span className="stock-dot" /> In stock — {product.stock} ready to dispatch</div>
          <div className="product-shipping-note"><FiTruck /><span><strong>Thoughtfully packed in {siteConfig.shipping.dispatchTime}</strong><br />Tracked delivery {formatPrice(siteConfig.shipping.flatRate)}.</span></div>

          <div className="product-accordions">
            <details open>
              <summary>Details <FiPlus /></summary>
              <dl>{Object.entries(product.attributes).map(([key, value]) => <React.Fragment key={key}><dt>{key}</dt><dd>{value}</dd></React.Fragment>)}</dl>
            </details>
            <details>
              <summary>Features & care <FiPlus /></summary>
              <ul>{product.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
            </details>
            <details>
              <summary>Delivery & returns <FiPlus /></summary>
              <p>Orders usually leave our studio within {siteConfig.shipping.dispatchTime}. Unused products in original condition may be returned within 30 days.</p>
            </details>
          </div>
        </aside>
      </div>

      {relatedProducts.length > 0 && (
        <section className="related-products page-shell section-space" aria-labelledby="related-title">
          <div className="section-heading section-heading--center"><p className="eyebrow">You may also like</p><h2 id="related-title">More from this collection</h2></div>
          <div className="related-grid">
            {relatedProducts.map((item) => (
              <article className="product-card" key={item.id}>
                <div className="product-card__media"><Link to={`/shopDetails/${item.id}`}><img src={item.image} alt={item.name} loading="lazy" decoding="async" width="720" height="720" /></Link></div>
                <div className="product-card__content"><p>{item.brand}</p><h3><Link to={`/shopDetails/${item.id}`}>{item.name}</Link></h3><div className="product-card__price"><span>{formatPrice(item.price)}</span></div></div>
              </article>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
